<template lang="pug">
    .creator-mention-dropdown-control(v-if="isOpen" :style="theStyle" v-click-outside="clickOutside" ref="theControl")
</template>

<script>
import CaretPositioner from 'textarea-caret-position'
import EventBus from '../../lib/event-bus'
import Events from '../../lib/events'
import ModalsList from '../modals/modals-list'

const mentionRegex = /^[a-z0-9\-_]+/i

export default {
    name: 'creator-mention-dropdown-control',

    props: {
        textEl: {
            type: [HTMLTextAreaElement, HTMLInputElement],
            required: true,
        },
    },

    data() {
        return {
            isOpen: false,
            keyDownWasOpen: false,
            elStyle: null,
            position: null,
            caretPositioner: null,
            modalComponent: null,
        }
    },

    computed: {
        theStyle() {
            return {
                'left': `${this.position.left + parseFloat(this.elStyle['padding-left']) + parseFloat(this.elStyle['margin-left'])}px`,
                'top': `${this.position.top + parseFloat(this.elStyle['padding-top']) + parseFloat(this.elStyle['margin-top'])}px`,
                'height': this.elStyle['line-height'],
            }
        },
    },

    methods: {
        keyup(ev) {
            switch(ev.key) {
                case '@':
                    this.open()
                    break
                case 'ArrowUp':
                    this.cancelEventIfOpen(ev)
                    break
                case 'ArrowDown':
                    this.cancelEventIfOpen(ev)
                    break
                case 'Enter':
                    this.cancelEventIfOpen(ev)
                    break
                case 'Escape':
                    this.cancelEventIfOpen(ev)
                    break
                default:
                    this.detectSearch({ reopen: true })
                    break
            }
        },
        keydown(ev) {
            this.keyDownWasOpen = this.isOpen

            switch (ev.key) {
                case 'ArrowUp':
                    this.selectPrev(ev)
                    break
                case 'ArrowDown':
                    this.selectNext(ev)
                    break
                case 'Enter':
                    this.useSelected(ev)
                    break
                case 'Escape':
                    this.close(ev)
                    break
            }
        },

        mouseclick() {
            this.detectSearch({ reopen: true })
        },

        open() {
            if (!this.isOpen) {
                this.isOpen = true
                this.detectSearch()
            }
        },
        close(ev) {
            if (this.isOpen) {
                this.isOpen = false
                this.modalComponent = null

                ev && this.cancelEvent(ev)

                EventBus.$emit(Events.CLOSE_MODAL, ModalsList.CREATOR_MENTION_DROPDOWN.level)
            }
        },

        detectSearch(opt) {
            opt = {
                reopen: false,
                ...opt,
            }

            if (this.isOpen || opt.reopen) {
                const info = this.getMentionInfo()

                if (!info) {
                    this.close()
                    return
                }

                const symbolPos = info.symbolPos
                const search = info.mention

                const position = this.caretPositioner.get(symbolPos, symbolPos)
                position.left -= this.textEl.scrollLeft
                position.top -= this.textEl.scrollTop

                if (position.left !== this.position?.left || position.top !== this.position?.top) {
                    EventBus.$emit(Events.CLOSE_MODAL, ModalsList.CREATOR_MENTION_DROPDOWN.level)
                }
                
                this.position = position

                if (opt.reopen) {
                    this.isOpen = true
                }

                this.$nextTick(async () => {
                    EventBus.$$emitAsync(Events.OPEN_MODAL, ModalsList.CREATOR_MENTION_DROPDOWN, {
                        element: this.$refs.theControl,
                        chooseCreatorCallback: this.useCreator,
                        disableAutoClose: () => {
                            return !this._isDestroyed
                        },
                    }).then(r => {
                        this.modalComponent = r[0].modalComponent
                        this.modalComponent.search(search)
                    })
                })
            }
        },

        getMentionInfo() {
            let value = this.textEl.value
            let pos = this.textEl.selectionEnd

            if (pos != this.textEl.selectionStart) {
                return
            }
            
            const symbolPos = value.lastIndexOf('@', pos)

            if (symbolPos < 0) {
                return
            }

            const startPos = symbolPos + 1
            value = value.substr(startPos)
            pos -= startPos

            if (pos < 0) {
                return
            }

            const match = value.match(mentionRegex)
            const mention = match ? match[0] : ''

            if (mention.length < pos || mention.length < 3) {
                return
            }

            return {
                startPos,
                endPos: startPos + mention.length,
                symbolPos,
                mention,
            }
        },

        cancelEventIfOpen(ev) {
            if (this.isOpen || this.keyDownWasOpen) {
                this.cancelEvent(ev)
            }
        },

        cancelEvent(ev) {
            ev.preventDefault()
            ev.stopPropagation()
        },

        selectNext(ev) {
            if (this.isOpen) {
                this.cancelEvent(ev)
                this.modalComponent.selectNext()
            }
        },

        selectPrev(ev) {
            if (this.isOpen) {
                this.cancelEvent(ev)
                this.modalComponent.selectPrev()
            }
        },

        useSelected(ev) {
            if (this.isOpen) {
                this.cancelEvent(ev)
                this.modalComponent.chooseSelectedCreator()
            }
        },

        useCreator(creator) {
            this.close()

            const info = this.getMentionInfo()
            const value = this.textEl.value

            this.textEl.value = `${value.substr(0, info.startPos)}${creator.username}${value.substr(info.endPos)}`
            this.textEl.dispatchEvent(new Event('input'))
            this.textEl.dispatchEvent(new Event('change'))
            this.textEl.focus()
            this.textEl.selectionStart = this.textEl.selectionEnd = info.startPos + creator.username.length
        },

        hookUpToTextElement() {
            this.textEl.addEventListener('keyup', this.keyup)
            this.textEl.addEventListener('keydown', this.keydown)
            this.textEl.addEventListener('click', this.mouseclick)
            this.elStyle = this.textEl.currentStyle || window.getComputedStyle(this.textEl)
            this.caretPositioner = new CaretPositioner(this.textEl)
        },

        forgetTextElement() {
            if (this.textEl) {
                this.textEl.removeEventListener('keyup', this.keyup)
                this.textEl.removeEventListener('keydown', this.keydown)
                this.textEl.removeEventListener('click', this.mouseclick)
            }
        },

        clickOutside(ev) {
            const elements = [this.textEl]
            if (this.modalComponent) {
                elements.push(this.modalComponent.$el)
            }

            const composedPath = ev.composedPath()

            if (!elements.some(el => composedPath.includes(el))) {
                this.close()
            }
        },
    },

    mounted() {
        this.hookUpToTextElement()
    },

    beforeDestroy() {
        this.forgetTextElement()
    },
}
</script>
